import { useState } from 'react';
import { Plus, Trash2, Image as ImageIcon, Loader2, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';
import ImageUploader from '../components/ImageUploader';

export default function AdminContent() {
  const { data: banners, loading, error, reload } = useAsync(
    () => api.get('/banners?all=true').then((r) => r.data),
    []
  );
  const [form, setForm] = useState({ title: '', subtitle: '', image: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title.trim()) return setFormError('Please enter a banner title.');
    if (!form.image) return setFormError('Please upload (or paste) a banner image.');

    setSaving(true);
    try {
      await api.post('/banners', form);
      setForm({ title: '', subtitle: '', image: '' });
      reload();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Could not save the banner. Check the server is running.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    setBusyId(b._id);
    try {
      await api.put(`/banners/${b._id}`, { active: !b.active });
      reload();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this banner?')) return;
    setBusyId(id);
    try {
      await api.delete(`/banners/${id}`);
      reload();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold">Content Management</h1>
        <p className="text-sm text-ink-400">Homepage banners & promotional content — reflects on Website + App.</p>
      </div>

      <form onSubmit={add} className="glass grid gap-4 p-6 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm text-ink-300">Title <span className="text-danger">*</span></span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Monsoon Wedding Offer"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand placeholder:text-ink-500" />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-ink-300">Subtitle</span>
          <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            placeholder="Optional tagline"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand placeholder:text-ink-500" />
        </label>
        <div className="sm:col-span-2">
          <ImageUploader
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            folder="banners"
            label="Banner Image *"
          />
        </div>

        {formError && <p className="text-sm text-danger sm:col-span-2">⚠ {formError}</p>}

        <button type="submit" disabled={saving} className="btn-primary sm:col-span-2 sm:w-max">
          {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Plus size={16} /> Add Banner</>}
        </button>
      </form>

      {loading ? (
        <Loader label="Loading banners…" />
      ) : error ? (
        <ErrorState message="Couldn't load banners." onRetry={reload} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {banners.map((b) => (
            <div key={b._id} className={`glass overflow-hidden ${b.active ? '' : 'opacity-60'}`}>
              <div className="relative h-40">
                <img src={b.image} alt={b.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 to-transparent" />
                <span className={`absolute left-3 top-3 rounded-pill px-2.5 py-1 text-[10px] font-semibold ${b.active ? 'bg-success/20 text-success' : 'bg-ink-700 text-ink-300'}`}>
                  {b.active ? 'Visible' : 'Hidden'}
                </span>
                <div className="absolute bottom-3 left-4 right-4">
                  <p className="font-display font-bold text-white">{b.title}</p>
                  <p className="text-xs text-ink-300">{b.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 p-3">
                <button
                  onClick={() => toggleActive(b)}
                  disabled={busyId === b._id}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition ${
                    b.active
                      ? 'border-white/10 text-ink-200 hover:bg-white/5'
                      : 'border-success/30 text-success hover:bg-success/10'
                  }`}
                >
                  {busyId === b._id ? <Loader2 size={13} className="animate-spin" /> : b.active ? <EyeOff size={13} /> : <Eye size={13} />}
                  {b.active ? 'Hide' : 'Show'}
                </button>
                <button onClick={() => remove(b._id)} className="flex items-center gap-1 rounded-lg border border-danger/30 px-3 py-1.5 text-xs text-danger hover:bg-danger/10">
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>
          ))}
          {banners.length === 0 && (
            <div className="glass col-span-full grid place-items-center gap-2 py-16 text-ink-400">
              <ImageIcon size={32} /> No banners yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
